import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWatch } from '../contexts/WatchContext';
import { useWatchedDeviceService } from '../hooks/useWatchedDeviceService';
import { useDeviceOnlineStatus } from '../hooks/useDeviceOnlineStatus';
import { toast } from 'react-hot-toast';

/**
 * Global Device Monitor Component
 * 
 * This component runs in the background across the entire application
 * to monitor device online status and provide real-time notifications.
 * It's invisible to the user but ensures device monitoring is always active.
 */
const GlobalDeviceMonitor = () => {
  const { currentUserData } = useAuth();
  const { getEffectiveUserId, isWatching } = useWatch();
  const { devices, getUserDevices } = useWatchedDeviceService();
  const {
    onlineStatusChanges,
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    clearStatusHistory
  } = useDeviceOnlineStatus();
  
  const isInitialized = useRef(false);

  // Initialize monitoring when user data is available
  useEffect(() => {
    const userId = getEffectiveUserId();
    if (userId && devices.length > 0 && !isInitialized.current) {
      isInitialized.current = true;
      
      // Start monitoring
      startMonitoring();
      
      // Clear any existing status history to start fresh
      clearStatusHistory();
    }
  }, [getEffectiveUserId(), devices.length, startMonitoring, clearStatusHistory]);

  // Cleanup monitoring when user changes or component unmounts
  useEffect(() => {
    return () => {
      if (isInitialized.current) {
        stopMonitoring();
        isInitialized.current = false;
      }
    };
  }, [stopMonitoring]);

  // Handle device status change events from timer
  useEffect(() => {
    const handleDeviceStatusChange = (event) => {
      console.log('🔍 GlobalDeviceMonitor - Device status change event received:', event.detail);
      
      // Force refresh of device data to update all components
      const refreshDeviceData = async () => {
        try {
          await getUserDevices(false); // Force fresh data
          console.log('✅ GlobalDeviceMonitor - Device data refreshed successfully');
        } catch (error) {
          console.error('❌ GlobalDeviceMonitor - Error refreshing device data:', error);
        }
      };
      
      refreshDeviceData();
      
      // Dispatch a custom event to notify Devices page specifically
      const devicesRefreshEvent = new CustomEvent('devices-data-refreshed', {
        detail: {
          deviceId: event.detail.deviceId,
          deviceName: event.detail.deviceName,
          oldStatus: event.detail.oldStatus,
          newStatus: event.detail.newStatus,
          timestamp: Date.now(),
          reason: 'status_change'
        }
      });
      window.dispatchEvent(devicesRefreshEvent);
      
      // Show toast notification for timer-triggered changes
      if (event.detail.triggeredBy === 'timer') {
        const { deviceName, newStatus } = event.detail;
        const status = newStatus === 'online' ? '🟢 Online' : '🔴 Offline';
        
        toast(`${deviceName} is now ${status} (Timer Check)`, {
          duration: 4000,
          icon: newStatus === 'online' ? '🟢' : '🔴',
          style: {
            background: newStatus === 'online' ? '#dcfce7' : '#fef2f2',
            color: newStatus === 'online' ? '#166534' : '#dc2626',
            border: `1px solid ${newStatus === 'online' ? '#bbf7d0' : '#fecaca'}`
          }
        });
      }
    };

    // Add event listener for device status changes
    window.addEventListener('device-status-changed', handleDeviceStatusChange);

    return () => {
      window.removeEventListener('device-status-changed', handleDeviceStatusChange);
    };
  }, [getUserDevices]);

  // Monitor for new device additions
  useEffect(() => {
    const handleNewDeviceAdded = (event) => {
      console.log('🆕 GlobalDeviceMonitor - New device added event received:', event.detail);
      
      // Force refresh of device data
      const refreshDeviceData = async () => {
        try {
          await getUserDevices(false); // Force fresh data
          console.log('✅ GlobalDeviceMonitor - Device data refreshed after new device addition');
        } catch (error) {
          console.error('❌ GlobalDeviceMonitor - Error refreshing device data after new device:', error);
        }
      };
      
      refreshDeviceData();
      
      // Dispatch refresh event
      const devicesRefreshEvent = new CustomEvent('devices-data-refreshed', {
        detail: {
          deviceId: event.detail.deviceId,
          deviceName: event.detail.deviceName,
          timestamp: Date.now(),
          reason: 'new_device_added'
        }
      });
      window.dispatchEvent(devicesRefreshEvent);
      
      // Show success notification
      toast.success(`New device "${event.detail.deviceName}" connected successfully!`, {
        duration: 5000,
        icon: '🆕'
      });
    };

    // Add event listener for new device additions
    window.addEventListener('new-device-added', handleNewDeviceAdded);

    return () => {
      window.removeEventListener('new-device-added', handleNewDeviceAdded);
    };
  }, [getUserDevices]);



  // This component doesn't render anything visible
  return null;
};

export default GlobalDeviceMonitor; 